import type { AxiosInstance } from 'axios';
import type { Plan } from '@/types';
import type { Discount, SettingsInvoice } from '@/types/settings';
import baseAxios from '@/helpers/forms/axios';

/**
 * Everything billing: the plan catalogue, the Stripe setup intent, the applied
 * coupon, invoices and the VAT tax rate. Subscribing, updating the payment
 * method and redeeming a coupon are writes and go through `sendForm()`.
 *
 * Three response shapes are easy to get wrong:
 *
 * - `/stripe/token` answers `{ clientSecret }`, not `{ client_secret }`.
 * - `/settings/invoices` returns local invoice rows keyed by `created_at`.
 * - `/coupon/user/{id}` answers a Stripe coupon object, or `204` when the
 *   billable has no discount.
 */
interface BillingApi {
    fetchPlans: () => Promise<Plan[]>
    fetchStripeSetupIntentSecret: () => Promise<string>
    fetchUserDiscount: (userId: number) => Promise<Discount | null>
    fetchTeamDiscount: (teamId: number, teamsPrefix?: string) => Promise<Discount | null>
    fetchInvoices: () => Promise<SettingsInvoice[]>
    invoiceDownloadUrl: (invoiceId: number | string) => string
    calculateTaxRate: (payload: Record<string, unknown>) => Promise<number>
    fetchStatesForCountry: (countryCode: string) => Promise<Record<string, string>>
}

const emptyToNull = (data: Discount | string | null): Discount | null => {
    if (data == null || typeof data === 'string') return null;

    return data;
};

export default (axios: AxiosInstance = baseAxios): BillingApi => ({
    fetchPlans: async (): Promise<Plan[]> => {
        const response = await axios.get<Plan[]>('/spark/plans');
        return response.data;
    },
    /**
     * Note the key: Spark's `StripeTokenController` answers with `clientSecret`,
     * not the snake-cased `client_secret` Stripe's own SetupIntent uses.
     */
    fetchStripeSetupIntentSecret: async (): Promise<string> => {
        const response = await axios.get<{ clientSecret: string }>('/stripe/token');
        return response.data.clientSecret;
    },
    fetchUserDiscount: async (userId: number): Promise<Discount | null> => {
        const response = await axios.get<Discount | string | null>(`/coupon/user/${userId}`);
        return emptyToNull(response.data);
    },
    fetchTeamDiscount: async (teamId: number, teamsPrefix: string = 'teams'): Promise<Discount | null> => {
        const response = await axios.get<Discount | string | null>(`/settings/${teamsPrefix}/coupon/${teamId}`);
        return emptyToNull(response.data);
    },
    fetchInvoices: async (): Promise<SettingsInvoice[]> => {
        const response = await axios.get<SettingsInvoice[]>('/settings/invoices');
        return response.data;
    },
    // A real Laravel route serving a PDF, so it is linked rather than fetched.
    invoiceDownloadUrl: (invoiceId: number | string): string => `/settings/invoice/${invoiceId}`,
    calculateTaxRate: async (payload: Record<string, unknown>): Promise<number> => {
        const response = await axios.post<{ rate: number }>('/tax-rate', payload);
        return response.data.rate;
    },
    fetchStatesForCountry: async (countryCode: string): Promise<Record<string, string>> => {
        const response = await axios.get<Record<string, string>>(`/geocode/states/${countryCode}`);
        return response.data;
    },
});
